import type { SchemaStationTimetable, SchemaTimetableSpan } from '../../schema/types'

export type DayKind = 'weekdays' | 'weekends'

/** One direction's row for the selected day kind. */
export interface TimetableRow {
  readonly towards: string
  /** `HH:MM` or null when the schema has no valid time (TASK-646 `--:--`). */
  readonly first: string | null
  readonly last: string | null
}

/** `HH:MM`. Anything else the schema carries is not a time (TASK-646). */
const CLOCK_TIME = /^\d{2}:\d{2}$/

/** Saturday/Sunday → weekends; the picker defaults from this. */
export function dayKindFor(date: Date): DayKind {
  const day = date.getDay()
  return day === 0 || day === 6 ? 'weekends' : 'weekdays'
}

function valid(time: string | undefined): string | null {
  return time !== undefined && CLOCK_TIME.test(time) ? time : null
}

/** After-midnight last trains belong to the evening service day. */
function serviceMinutes(time: string, lastAxis: boolean): number {
  const [h = 0, m = 0] = time.split(':').map(Number)
  const minutes = h * 60 + m
  return lastAxis && h < 3 ? minutes + 24 * 60 : minutes
}

function earliest(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return serviceMinutes(b, false) < serviceMinutes(a, false) ? b : a
}

function latest(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return serviceMinutes(b, true) > serviceMinutes(a, true) ? b : a
}

/**
 * Rows of the Timetable section (Figma 8597:41665) for one day kind.
 * Weekdays read the weekday span verbatim; weekends MERGE Saturday and
 * Sunday — the earliest first train and the latest last train describe
 * the whole weekend the way the design's single time column expects.
 */
export function timetableForDayKind(
  timetable: SchemaStationTimetable | undefined,
  kind: DayKind,
): TimetableRow[] {
  if (timetable === undefined) return []
  return timetable.directions
    .map((direction) => {
      const towards = direction.destinationStationLocalName ?? ''
      if (kind === 'weekdays') {
        const span: SchemaTimetableSpan = direction.weekdays ?? {}
        return { towards, first: valid(span.firstTrain), last: valid(span.lastTrain) }
      }
      const sat = direction.saturdays ?? {}
      const sun = direction.sundays ?? {}
      return {
        towards,
        first: earliest(valid(sat.firstTrain), valid(sun.firstTrain)),
        last: latest(valid(sat.lastTrain), valid(sun.lastTrain)),
      }
    })
    .filter((row) => row.towards !== '')
}
